import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const FeatureContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '46px 76px',
  backgroundColor: '#1e293b', // slate-800
  border: '1px solid #334155', // slate-700
  borderRadius: '24px',
  overflow: 'visible',
  [theme.breakpoints.down('lg')]: {
    padding: '40px 40px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '30px 20px',
    borderRadius: '20px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px 15px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '556px',
  height: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '97px',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    gap: '50px',
    minHeight: '450px'
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
    gap: '30px',
    minHeight: 'auto'
  }
}));

const ImageContainer = styled(Box)(({ theme, isVisible }) => ({
  position: 'relative',
  width: '718px',
  maxWidth: '100%',
  height: '508px',
  borderRadius: '24px',
  border: '2px solid transparent',
  backgroundImage: 'url(/images/leaderboard.jpeg), linear-gradient(90deg, #22d3ee, #a5b4fc)',
  backgroundSize: 'cover, 100% 100%',
  backgroundPosition: 'center, 0 0',
  backgroundOrigin: 'padding-box, border-box',
  backgroundClip: 'padding-box, border-box',
  overflow: 'hidden',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(-100px)',
  transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
  flexShrink: 0,
  [theme.breakpoints.down('lg')]: {
    width: '600px',
    height: '420px'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: '350px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '280px',
    borderRadius: '16px'
  }
}));

const TextContent = styled(Box)(({ theme, isVisible }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '465px',
  maxWidth: '100%',
  alignItems: 'flex-start',
  gap: '33px',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(100px)',
  transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
  [theme.breakpoints.down('lg')]: {
    width: '400px'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    alignItems: 'center'
  }
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '42px',
  letterSpacing: '0',
  lineHeight: '54px',
  marginBottom: '24px',
  display: 'block',
  width: '100%',
  '& .black': {
    color: '#ffffff'
  },
  '& .blue': {
    background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '36px',
    lineHeight: '46px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '30px',
    lineHeight: '40px',
    textAlign: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    lineHeight: '34px'
  }
}));

const FeaturesList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '247px',
  height: 'auto',
  alignItems: 'flex-start',
  gap: '16px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto'
  }
}));

const FirstFeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  width: '100%'
}));

const FeatureItem = styled(Box)(({ theme, height }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  width: '100%',
  minHeight: height || 'auto'
}));

const FeatureIcon = styled('img')(({ theme }) => ({
  width: '20px',
  height: '18px'
}));

const FeatureText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  letterSpacing: '0',
  lineHeight: '24px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  flex: 1,
  '& .title': {
    fontWeight: 600,
    color: '#ffffff',
    marginRight: '4px'
  },
  '& .description': {
    fontWeight: 400,
    color: '#94a3b8'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '22px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    lineHeight: '20px'
  }
}));

const CheckButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  width: '180px',
  height: '48px',
  borderRadius: '27px',
  boxShadow: '0px 10px 30px rgba(212, 212, 212, 0.3)',
  background: 'linear-gradient(119deg, rgba(11, 145, 214, 1) 0%, rgba(239, 35, 42, 1) 100%)',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  color: '#ffffff',
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: '16px',
  textTransform: 'none',
  WebkitTextStroke: '1px #A0A0AA',
  '&:hover': {
    background: 'linear-gradient(119deg, rgba(11, 145, 214, 0.9) 0%, rgba(239, 35, 42, 0.9) 100%)',
    boxShadow: '0px 12px 35px rgba(212, 212, 212, 0.4)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '150px',
    height: '42px',
    fontSize: '14px'
  }
}));

const LeaderboardFeature = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const handleCheckNowClick = () => {
    navigate('/login');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <FeatureContainer ref={containerRef}>
      <ContentWrapper>
        <ImageContainer isVisible={isVisible}>
          {/* Background image is set in ImageContainer styled component */}
        </ImageContainer>

        <TextContent isVisible={isVisible}>
          <MainTitle>
            <span className="black">India's First Dynamic</span>
            <br />
            <span className="black">Local Business</span>
            <br />
            <span className="blue">Leaderboard</span>
          </MainTitle>

          <FeaturesList>
            <FirstFeatureItem>
              <FeatureIcon src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/icon.svg" alt="Icon" />
              <FeatureText>
                <span className="title">Micro-Level Scoring -</span>
                <span className="description">Every review, photo, post, and update contributes to your position.</span>
              </FeatureText>
            </FirstFeatureItem>

            <FeatureItem>
              <FeatureIcon src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/icon.svg" alt="Icon" />
              <FeatureText>
                <span className="title">Real-Time Dynamics -</span>
                <span className="description">Your score shifts instantly as your profile or competitors change.</span>
              </FeatureText>
            </FeatureItem>

            <FeatureItem>
              <FeatureIcon src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/icon.svg" alt="Icon" />
              <FeatureText>
                <span className="title">Hyperlocal Benchmarking -</span>
                <span className="description">Compare against businesses in your exact city & category.</span>
              </FeatureText>
            </FeatureItem>
          </FeaturesList>

          <CheckButton onClick={handleCheckNowClick}>
            Check Now
          </CheckButton>
        </TextContent>
      </ContentWrapper>
    </FeatureContainer>
  );
};

export default LeaderboardFeature;
